import { createFileRoute, Link } from "@tanstack/react-router";
import { useSiteData } from "@/lib/use-site-data";
import { Breadcrumbs, breadcrumbJsonLd } from "@/components/Breadcrumbs";

type FAQ = { q: string; a: string };

const faqs: FAQ[] = [
  {
    q: "¿Cuántas personas caben en la casa?",
    a: "El Pajar de Benito es una casa rural completa con capacidad para hasta 14 personas en 6 habitaciones, todas con baño privado. Es ideal para grupos de amigos, familias o reuniones.",
  },
  {
    q: "¿Se alquila la casa entera o por habitaciones?",
    a: "Se alquila la casa al completo en régimen de uso exclusivo. No compartirás la vivienda con otros huéspedes durante tu estancia.",
  },
  {
    q: "¿Cuál es la estancia mínima?",
    a: "Por norma general el mínimo son 2 noches. En puentes, festivos y temporada alta puede subir a 3 noches. Consúltanos en el formulario de reserva tus fechas concretas.",
  },
  {
    q: "¿Se admiten mascotas?",
    a: "Por respeto a la casa y a futuros huéspedes, actualmente no se admiten mascotas. Si tienes una situación especial, escríbenos por WhatsApp y lo valoramos.",
  },
  {
    q: "¿Cuál es el horario de check-in y check-out?",
    a: "La entrada (check-in) es a partir de las 16:00 y la salida (check-out) hasta las 12:00. Si necesitas otro horario, intentaremos adaptarnos según ocupación.",
  },
  {
    q: "¿Cómo se paga la reserva?",
    a: "La reserva se confirma con pago online por tarjeta a través de Redsys (pasarela bancaria segura). No cobramos comisiones por reservar directo en la web.",
  },
  {
    q: "¿Qué incluye el precio?",
    a: "El alquiler incluye ropa de cama y toallas, calefacción, WiFi gratis, leña para la chimenea, cocina equipada (vajilla, menaje, electrodomésticos) y limpieza final.",
  },
  {
    q: "¿Hay parking?",
    a: "Sí, dispones de aparcamiento gratuito en la propia parcela y en las calles del pueblo. Recomendamos venir en coche porque el transporte público hasta El Ordial es muy limitado.",
  },
  {
    q: "¿Cómo se llega a El Ordial desde Madrid?",
    a: "Desde Madrid son unas 2 horas por la A-2 hasta Guadalajara, después la CM-1006 dirección Cogolludo y desvío hacia El Ordial. Te enviaremos indicaciones detalladas tras la reserva.",
  },
  {
    q: "¿Hay supermercado o restaurantes cerca?",
    a: "En El Ordial no hay tiendas. Los supermercados y restaurantes más cercanos están en Hiendelaencina, Cogolludo y Atienza (15-30 min en coche). Te recomendaremos restaurantes locales si quieres reservar mesa.",
  },
  {
    q: "¿Qué política de cancelación tenéis?",
    a: "Cancelación gratuita hasta 30 días antes de la llegada. Entre 30 y 15 días se devuelve el 50%. A menos de 15 días no es reembolsable. Escríbenos si surge un imprevisto y buscaremos opciones.",
  },
  {
    q: "¿Qué planes hay en la zona?",
    a: "El Pico Alto Rey, el Hayedo de Tejera Negra, los pueblos negros (Majaelrayo, Valverde, Campillo), la Ciudad Encantada de Tamajón, Atienza medieval y la observación de estrellas. Más detalle en la sección Entorno y en el blog.",
  },
];

export const Route = createFileRoute("/faq")({
  head: () => ({
    meta: [
      { title: "Preguntas frecuentes · El Pajar de Benito" },
      { name: "description", content: "Respuestas a las dudas habituales antes de reservar: capacidad, mínimo de noches, mascotas, check-in, parking, cómo llegar a El Ordial y política de cancelación." },
      { property: "og:title", content: "Preguntas frecuentes · El Pajar de Benito" },
      { property: "og:description", content: "Capacidad, reserva, mascotas, horarios, parking, cómo llegar y cancelación. Todo lo que necesitas saber antes de venir." },
      { property: "og:url", content: "/faq" },
    ],
    links: [{ rel: "canonical", href: "https://elpajardebenito.es/faq" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbJsonLd([
            { label: "Inicio", to: "/" },
            { label: "Preguntas frecuentes", to: "/faq" },
          ]),
        ),
      },
    ],
  }),
  component: FAQPage,
});

function FAQPage() {
  const { config } = useSiteData();
  const waNumber = (config.whatsapp ?? "").replace(/\D/g, "");
  return (
    <>
    <Breadcrumbs items={[{ label: "Inicio", to: "/" }, { label: "FAQ" }]} />
    <section className="mx-auto max-w-3xl px-4 py-20 md:px-6 md:py-28">
      <p className="text-xs uppercase tracking-[0.28em] text-accent">Ayuda</p>
      <h1 className="mt-3 font-serif text-4xl text-foreground md:text-5xl">Preguntas frecuentes</h1>
      <p className="mt-5 text-muted-foreground">
        Las dudas que más nos preguntáis antes de reservar. Si no encuentras la respuesta, escríbenos por WhatsApp o por email y te respondemos en menos de 24 horas.
      </p>
      <div className="mt-12 divide-y divide-border border-t border-border">
        {faqs.map((f) => (
          <details key={f.q} className="group py-5">
            <summary className="flex cursor-pointer items-start justify-between gap-4 text-left font-serif text-lg text-foreground">
              <span>{f.q}</span>
              <span className="mt-1 text-accent transition-transform group-open:rotate-45">+</span>
            </summary>
            <p className="mt-3 leading-relaxed text-muted-foreground">{f.a}</p>
          </details>
        ))}
      </div>
      <div className="mt-14 rounded-sm border border-border bg-secondary/40 p-6 text-center">
        <p className="text-sm text-muted-foreground">¿Sigues con dudas?</p>
        <div className="mt-4 flex flex-wrap justify-center gap-3">
          {waNumber && (
            <a
              href={`https://wa.me/${waNumber}`}
              target="_blank"
              rel="noopener"
              className="rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              WhatsApp
            </a>
          )}
          <Link
            to="/contacto"
            className="rounded-full border border-primary px-6 py-3 text-sm font-medium text-primary hover:bg-primary/5"
          >
            Contacto
          </Link>
        </div>
      </div>
    </section>
    </>
  );
}